import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { Globe, Loader2, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function IpAddresses() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingIp, setEditingIp] = useState<{ id: number; name: string; ipAddress: string; description: string | null } | null>(null);
  const [newIp, setNewIp] = useState({ name: "", ipAddress: "", description: "" });
  const [detectedIp, setDetectedIp] = useState<string | null>(null);
  const [detectingIp, setDetectingIp] = useState(false);

  const { data: savedIps, isLoading, refetch } = trpc.savedIps.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const createMutation = trpc.savedIps.create.useMutation({
    onSuccess: () => {
      toast.success("IP address saved");
      setIsCreateOpen(false);
      setNewIp({ name: "", ipAddress: "", description: "" });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.savedIps.update.useMutation({
    onSuccess: () => {
      toast.success("IP address updated");
      setIsEditOpen(false);
      setEditingIp(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.savedIps.delete.useMutation({
    onSuccess: () => {
      toast.success("IP address deleted");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDetectIp = async () => {
    setDetectingIp(true);
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      setDetectedIp(data.ip);
      setNewIp(prev => ({ ...prev, ipAddress: data.ip }));
    } catch (error) {
      toast.error("Failed to detect IP address");
    } finally {
      setDetectingIp(false);
    }
  };

  const handleCreate = () => {
    if (!newIp.name.trim() || !newIp.ipAddress.trim()) {
      toast.error("Name and IP address are required");
      return;
    }
    createMutation.mutate({
      name: newIp.name,
      ipAddress: newIp.ipAddress,
      description: newIp.description || undefined,
    });
  };

  const handleUpdate = () => {
    if (!editingIp) return;
    updateMutation.mutate({
      id: editingIp.id,
      name: editingIp.name,
      ipAddress: editingIp.ipAddress,
      description: editingIp.description || undefined,
    });
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this saved IP?")) {
      deleteMutation.mutate({ id });
    }
  };

  if (authLoading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!isAuthenticated) {
    window.location.href = "/";
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="IP Addresses"
          subtitle="Manage your saved IP addresses for quick reuse across secrets"
          action={
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add IP Address
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New IP Address</DialogTitle>
                  <DialogDescription>
                    Save an IP address for quick reuse when whitelisting secrets.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Office, Home, AWS Lambda"
                      value={newIp.name}
                      onChange={(e) => setNewIp(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ipAddress">IP Address</Label>
                    <div className="flex gap-2">
                      <Input
                        id="ipAddress"
                        placeholder="e.g., 192.168.1.1"
                        value={newIp.ipAddress}
                        onChange={(e) => setNewIp(prev => ({ ...prev, ipAddress: e.target.value }))}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDetectIp}
                        disabled={detectingIp}
                      >
                        {detectingIp ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <MapPin className="h-4 w-4 mr-1" />
                            Find My IP
                          </>
                        )}
                      </Button>
                    </div>
                    {detectedIp && (
                      <p className="text-sm text-muted-foreground">
                        Detected: {detectedIp}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="Add notes about this IP..."
                      value={newIp.description}
                      onChange={(e) => setNewIp(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending}>
                    {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save IP Address
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="size-8 text-primary" />
          </div>
        ) : savedIps && savedIps.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {savedIps.map((ip) => (
              <Card key={ip.id} className="border-border/50">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Globe className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{ip.name}</CardTitle>
                        <CardDescription className="font-mono text-sm">
                          {ip.ipAddress}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditingIp({
                            id: ip.id,
                            name: ip.name,
                            ipAddress: ip.ipAddress,
                            description: ip.description,
                          });
                          setIsEditOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(ip.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {ip.description && (
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground">{ip.description}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Globe />
              </EmptyMedia>
              <EmptyTitle>No saved IP addresses</EmptyTitle>
              <EmptyDescription>
                Save your frequently used IP addresses here for quick access when whitelisting secrets.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First IP
              </Button>
            </EmptyContent>
          </Empty>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit IP Address</DialogTitle>
              <DialogDescription>
                Update the saved IP address details.
              </DialogDescription>
            </DialogHeader>
            {editingIp && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editingIp.name}
                    onChange={(e) => setEditingIp(prev => prev ? { ...prev, name: e.target.value } : null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-ipAddress">IP Address</Label>
                  <Input
                    id="edit-ipAddress"
                    value={editingIp.ipAddress}
                    onChange={(e) => setEditingIp(prev => prev ? { ...prev, ipAddress: e.target.value } : null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description (optional)</Label>
                  <Textarea
                    id="edit-description"
                    value={editingIp.description || ""}
                    onChange={(e) => setEditingIp(prev => prev ? { ...prev, description: e.target.value } : null)}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
